import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";

type UpdateCompositionConfirmDialogProps = {
	investmentName: string;
	/** `true` when the commit will also persist the lab's session AUM onto the real portfolio. */
	willUpdateAum: boolean;
	onConfirm(): void;
};

function UpdateCompositionConfirmDialog({
	show,
	onClose,
	onAnimationStateChange,
	investmentName,
	willUpdateAum,
	onConfirm,
}: UpdateCompositionConfirmDialogProps & MinimumDialogProps) {
	return (
		<Dialog
			show={show}
			onClose={onClose}
			header="Update composition"
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier="PortfolioLab/UpdateComposition/Cancel">
							Cancel
						</Button>
					}
					primaryAction={<SubmitButton data-qualifier="PortfolioLab/UpdateComposition/Confirm">Update</SubmitButton>}
				/>
			}
			onSubmitAsync={() => {
				onConfirm();
			}}
		>
			<Text type="Body/M/Book" as="p">
				This will overwrite the saved composition of <b>{investmentName}</b> with the current one from the lab.
				{willUpdateAum && <> The portfolio&apos;s AUM will also be updated to the value set in the lab.</>} This action
				cannot be undone.
			</Text>
		</Dialog>
	);
}

/** Confirm before committing a lab composition to the real investment. Resolves `true` on confirm, `false` otherwise. */
export function spawnUpdateCompositionConfirmDialog(
	props: Pick<UpdateCompositionConfirmDialogProps, "investmentName" | "willUpdateAum">,
): SpawnResult<boolean> {
	return spawn<boolean>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<UpdateCompositionConfirmDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve(false)}
				onConfirm={() => resolve(true)}
			/>
		)),
	);
}
